import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Target, Brain, Lightbulb, GraduationCap, Heart, Repeat, Sparkles,
  Users, Moon, Zap, ShieldCheck, ArrowRight, BookOpen, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  target: Target,
  brain: Brain,
  lightbulb: Lightbulb,
  'graduation-cap': GraduationCap,
  heart: Heart,
  repeat: Repeat,
  sparkles: Sparkles,
  users: Users,
  moon: Moon,
  zap: Zap,
};

export default function CognoGuruLanding() {
  const [gurus, setGurus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/cognoguru/gurus`).then((r) => {
      setGurus(r.data.gurus || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="cognoguru-landing">
      {/* Hero */}
      <section className="border-b border-[#09090B] bg-[#09090B] text-white py-20 md:py-28">
        <div className="container-swiss">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="w-4 h-4 text-white" />
            <span className="mono-data text-white">Premium Intelligence Layer · CognoGuru Safe™</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-6 max-w-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Expert-Guided Cognitive Intelligence Companion
          </h1>
          <p className="text-lg md:text-xl text-zinc-300 leading-relaxed max-w-3xl mb-8">
            Ask safe, age-appropriate questions about focus, memory, learning, thinking, habits, emotional balance, and cognitive development — guided by expert-curated knowledge from NIH, Harvard, Stanford, APA, WHO, and peer-reviewed research.
          </p>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <a href="#gurus" className="inline-flex items-center gap-3 bg-white text-[#09090B] px-6 py-4 text-xs font-bold tracking-widest uppercase hover:bg-zinc-200 transition-colors" data-testid="meet-gurus-cta">
              Meet the 10 CognoGurus <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/pricing" className="inline-flex items-center gap-3 border border-white px-6 py-4 text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-[#09090B] transition-colors">
              Upgrade for Unlimited
            </Link>
          </div>
          <p className="mono-data text-[10px] text-zinc-500 mt-6 max-w-2xl">
            Free tier: 3 questions/day · Pro: Unlimited · Always citation-backed · Never a medical diagnosis.
          </p>
        </div>
      </section>

      {/* What it is / what it's not */}
      <section className="py-16 md:py-20 border-b border-[#09090B]">
        <div className="container-swiss">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-[#09090B] p-8" data-testid="what-it-is">
              <p className="mono-data text-[#09090B] mb-3">What it is</p>
              <h3 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                An expert-curated cognitive intelligence system
              </h3>
              <ul className="space-y-2 text-sm text-zinc-700 leading-relaxed">
                <li className="flex gap-2"><span className="text-[#09090B] font-bold">→</span> 10 specialized AI Gurus, each grounded in a domain</li>
                <li className="flex gap-2"><span className="text-[#09090B] font-bold">→</span> Citation-backed answers from verified sources</li>
                <li className="flex gap-2"><span className="text-[#09090B] font-bold">→</span> Child-safe, age-appropriate, anxiety-aware</li>
                <li className="flex gap-2"><span className="text-[#09090B] font-bold">→</span> Built into your FOKYES practice</li>
              </ul>
            </div>
            <div className="border border-zinc-300 p-8 bg-zinc-50" data-testid="what-its-not">
              <p className="mono-data text-zinc-500 mb-3">What it&apos;s not</p>
              <h3 className="text-2xl font-bold tracking-tight mb-4 text-zinc-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Not a generic chatbot
              </h3>
              <ul className="space-y-2 text-sm text-zinc-600 leading-relaxed">
                <li className="flex gap-2"><span className="text-zinc-400">✕</span> Not a medical or therapeutic professional</li>
                <li className="flex gap-2"><span className="text-zinc-400">✕</span> Not for diagnoses or prescriptions</li>
                <li className="flex gap-2"><span className="text-zinc-400">✕</span> Not for off-topic, harmful, or alarming content</li>
                <li className="flex gap-2"><span className="text-zinc-400">✕</span> Not a substitute for a parent, teacher, or counselor</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Council of 10 */}
      <section id="gurus" className="py-20 md:py-28 border-b border-[#09090B]">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">The Council of 10</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Meet your CognoGurus
          </h2>
          <p className="text-base text-zinc-600 max-w-2xl mb-12 leading-relaxed">
            Each Guru is a domain specialist. Pick one, ask a question, get an answer grounded in published research — never AI hallucination.
          </p>

          {loading ? (
            <div className="mono-data text-zinc-400">Loading the Council...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gurus.map((g, i) => {
                const Icon = ICON_MAP[g.icon] || Brain;
                return (
                  <div key={g.guru_id} className="border border-[#09090B] p-6 flex flex-col hover:bg-[#09090B] hover:text-white transition-colors group" data-testid={`guru-card-${g.guru_id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 border border-[#09090B] group-hover:border-white flex items-center justify-center transition-colors">
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <span className="mono-data text-[10px] text-zinc-400 group-hover:text-zinc-500">
                        {String(i + 1).padStart(2, '0')} / 10
                      </span>
                    </div>
                    <h3 className="text-xl font-bold tracking-tight mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {g.name}
                    </h3>
                    <p className="mono-data text-xs text-zinc-500 group-hover:text-zinc-400 mb-3">{g.tagline}</p>
                    <p className="text-xs leading-relaxed text-zinc-700 group-hover:text-zinc-200 mb-6 flex-1">
                      {g.short_description}
                    </p>
                    <Link
                      to={`/cognoguru/${g.guru_id}`}
                      className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase border-b border-[#09090B] group-hover:border-white pb-1 self-start"
                      data-testid={`guru-ask-${g.guru_id}`}
                    >
                      Ask {g.name.split(' ')[0]} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 border-b border-[#09090B]">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-3">How it works</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Curated knowledge → AI Guru → Cited answer
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Verified Library', desc: 'Each Guru has a hand-curated knowledge base from NIH, Harvard, Stanford, APA, and peer-reviewed research.', icon: BookOpen },
              { step: '02', title: 'Safe Reasoning', desc: 'Your question is answered with curated context + safety guardrails — no medical diagnosis, no harmful content, no fear-language.', icon: ShieldCheck },
              { step: '03', title: 'Cited Response', desc: 'Every factual claim is backed by a real research citation with a working URL. No "trust me" — only "see for yourself".', icon: AlertCircle },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.step} className="border border-[#09090B] p-8">
                  <Icon className="w-5 h-5 text-[#09090B] mb-4" />
                  <span className="mono-data text-[#09090B] text-2xl font-black">{s.step}</span>
                  <h3 className="text-lg font-semibold tracking-tight mt-3 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 border-b border-[#09090B] bg-zinc-50">
        <div className="container-swiss">
          <div className="max-w-3xl">
            <p className="mono-data text-zinc-500 mb-3">Important</p>
            <p className="text-base text-zinc-700 leading-relaxed">
              <strong>CognoGuru Safe</strong> provides educational and cognitive development guidance only. It does not diagnose, treat, or replace professional medical or psychological care. If you ever feel unsafe, persistently anxious, or are thinking about hurting yourself, please tell a trusted adult or call a mental-health helpline immediately. <strong>India: iCall 9152987821 · US: 988</strong>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
